<template>
	<div class="widget widget-myarea" :class="{ 'fullscreen-widget': isFullscreen }" ref="widgetRootRef">
		<v-card class="mx-auto" flat>
			<template v-if="!isLoading">
				<v-toolbar color="transparent" @click="toggleFullscreen">
					<div class="v-card-top-actions" v-if="isFullscreen">
						<span>
							<md-icon>chevron_left</md-icon>
							{{ t('Back') }}
						</span>
					</div>
					<v-toolbar-title>
						<md-icon>location_on</md-icon>
						{{ title }}
					</v-toolbar-title>
					<div class="v-card-top-actions">
						<md-icon v-if="!isFullscreen">arrow_circle_right</md-icon>
					</div>
				</v-toolbar>

				<v-card-text>
					<MapLibre
						ref="mapRef"
						:locations="deliveryLocationsDisplayed"
						:initial-center="center"
						:initial-zoom="zoom"
						:detail-marker-renderer="renderMarkerDetail"
						:tool-box="['offline']"
						:filter-component="DeliveryLocationFiltersDialog"
						:selected-date="selectedDate"
						@filters-changed="onFiltersChanged"
						@marker-click="onMarkerClick"
						@bounds-changed="onBoundsChanged"
						@action-focus="handleActionFocus"
						@edit-delivery-location="(id) => handleEditDeliveryLocation(id, () => isFullscreen.value && toggleFullscreen())"
						@add-new-visit="handleAddNewVisit"
						@phone-number-copied="handlePhoneNumberCopied"
					/>
				</v-card-text>
			</template>

			<template v-else>
				<v-skeleton-loader type="list-item, list-item-two-line" />
			</template>
		</v-card>
	</div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, inject, nextTick } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/utils/useTranslator';
import { eventBus } from '@/utils/eventBus';
import { useVisibleRefresh } from '@/utils/useVisibleRefresh';
import { handleEditDeliveryLocation, handleAddNewVisit, handlePhoneNumberCopied, getDeliveryLocationTypeColor, getDeliveryLocationKPIs, getVisitFrequency, getNextVisitInfo, getLastVisitInfo, resetDetailMarkerData } from '@/utils/appointmentUtils';
import { appointmentService } from '@/services/appointmentService';

import MapLibre from '@/components/map/MapLibre.vue';
import DeliveryLocationFiltersDialog from '@/components/dialogs/DeliveryLocationFiltersDialog.vue';

// #region DATA

const t = useTranslator();
const dateHelper = iframeDataBridge.getDateHelper();
const enumAppointment = iframeDataBridge.enumAppointment;
const user = iframeDataBridge.getCurrentUser();

const isFullscreen = ref(false);
const mapRef = ref(null);
const widgetRootRef = ref(null);
const center = ref([2.3522, 48.8566]);
const zoom = ref(6);
const lastObservedHeight = ref(0);

let widgetResizeObserver = null;

const mapControl = inject('mapControl');

const props = defineProps({
	title: String,
	selectedDate: Date,
});

const isLoading = ref(true);
const deliveryLocations = ref([]);
const deliveryLocationsDisplayed = ref([]);
// #endregion

// #region LIFECYCLE

useVisibleRefresh(['appointments-updated', 'delivery-location-created'], init);

onMounted(() => {
	init();
	initWidgetResizeObserver();

	mapControl.zoomToLocation = (locationId) => {
		const location = deliveryLocations.value.find((dl) => dl.id === locationId);
		if (location && location._geo && mapRef.value) {
			mapRef.value.zoomTo(location._geo.latitude, location._geo.longitude);
		}
	};

	mapControl.drawRoutes = async (locations) => {
		if (mapRef.value) {
			await getDeliveryLocationGeoCoordinates(locations);
			mapRef.value.drawRoutes(locations);
		}
	};

	window.addEventListener('resize', handleWindowResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleWindowResize);
	destroyWidgetResizeObserver();
});

// #endregion

// #region METHODS

function init() {
	getMapData().then(() => {
		isLoading.value = false;
		resizeWidgetAndMap();
	});
}

async function getMapData(additionalLocations = []) {
	return new Promise(async (resolve, reject) => {
		try {
			const callback = getMapData;

			if (iframeDataBridge.isSyncEnded(['DELIVERYLOCATIONS'], callback)) {
				const startDate = new Date(props.selectedDate);
				startDate.setHours(0, 0, 0, 0);
				const endDate = new Date(startDate);
				endDate.setHours(23, 59, 59, 999);

				const success = async (result) => {
					try {
						const appointments = result.filter((appointment) => {
							return appointment.status !== enumAppointment.status.CANCELLED && dateHelper.isDateGreater(appointment.startDate, startDate, true) && dateHelper.isDateLess(appointment.startDate, endDate, true);
						});

						// Get all delivery locations
						deliveryLocations.value = iframeDataBridge.getEcosVM().deliveryLocations.map((dl) => ({
							...dl,
							appointments: appointments.filter((a) => a.deliveryLocationId === dl.id),
						}));

						getUserHome(deliveryLocations.value);

						if (additionalLocations?.length) {
							deliveryLocations.value = [...deliveryLocations.value, ...additionalLocations];
						}

						await getDeliveryLocationGeoCoordinates(deliveryLocations.value);

						deliveryLocations.value.forEach((dl) => {
							if (!dl.color) {
								dl.color = getDeliveryLocationTypeColor(dl);
							}
						});

						deliveryLocationsDisplayed.value = deliveryLocations.value.slice();
						resolve();
					} catch (err) {
						reject(err);
					}
				};

				const params = {
					startDate: startDate.toISOString(),
					endDate: endDate.toISOString(),
					userId: user.id,
				};

				await appointmentService.getAppointments(params, success);
			} else {
				resolve();
			}
		} catch (error) {
			reject(error);
		}
	});
}

function getUserHome(deliveryLocations) {
	if (!deliveryLocations?.length) return;

	let userHome = iframeDataBridge.getEcosVM().deliveryLocations.filter((dl) => dl.id === user.deliveryLocationHomeId);
	if (userHome.length) {
		deliveryLocations.unshift(userHome[0]);
	}
}

function getDeliveryLocationGeoCoordinates(deliveryLocations) {
	if (!deliveryLocations?.length) return Promise.resolve();

	return new Promise((resolve) => {
		iframeDataBridge.getStorage().get('DELIVERYLOCATIONGEO', (geoInfos) => {
			deliveryLocations.forEach((location) => {
				const geoInfo = geoInfos.find((geo) => geo.deliveryLocationId === location.id);
				if (geoInfo) {
					location._geo = { latitude: geoInfo.latitude, longitude: geoInfo.longitude };
					location.lat = geoInfo.latitude;
					location.lng = geoInfo.longitude;
				} else {
					location._geo = null;
					location.lat = null;
					location.lng = null;
				}
			});
			resolve();
		});
	});
}

async function renderMarkerDetail(markerData, iframeDocument, detailMarkerData) {
	resetDetailMarkerData(detailMarkerData, markerData);

	detailMarkerData.value.title = markerData.label;
	detailMarkerData.value.id = markerData.id;
	detailMarkerData.value.phoneNumber = markerData.phoneNumber || null;

	const addressParts = [];
	if (markerData.addressLine) addressParts.push(markerData.addressLine);
	if (markerData.addressLine2) addressParts.push(markerData.addressLine2);
	if (markerData.city) addressParts.push(markerData.city);
	if (markerData.postcode) addressParts.push(markerData.postcode);
	if (markerData.country) addressParts.push(markerData.country);
	detailMarkerData.value.address = addressParts.join(', ');

	try {
		const sfaHelper = iframeDataBridge.getSfaHelper();
		if (sfaHelper && sfaHelper.kpi && sfaHelper.kpi.templates) {
			const spsValue = await sfaHelper.kpi.templates.getSPSValue(markerData.id);
			detailMarkerData.value.sps = spsValue ?? null;
		} else {
			detailMarkerData.value.sps = null;
		}
	} catch (e) {
		detailMarkerData.value.sps = null;
	}

	if (markerData.accountId) {
		const account = iframeDataBridge
			.getEcosVM()
			.accounts()
			.find((a) => a.id === markerData.accountId);
		if (account && account.imageUId && account.imageUId !== '00000000-0000-0000-0000-000000000000') {
			try {
				if (iframeDataBridge.isOnline()) {
					const { getAccountImageUrl } = await import('@/api/core/file');
					const imageUrl = await getAccountImageUrl(account.imageUId);
					detailMarkerData.value.accountLogo = imageUrl || null;
					detailMarkerData.value.accountName = account.Name || '';
				}
			} catch (error) {
				console.error('Error loading account logo:', error);
			}
		}
	}

	const fields = {};

	const deliveryLocationTypes = iframeDataBridge.getEcosVM().deliveryLocationTypes;
	const deliveryLocationType = deliveryLocationTypes?.find((type) => type.id === markerData.deliveryLocationTypeId);
	if (deliveryLocationType) {
		fields[t('Type')] = deliveryLocationType.name || deliveryLocationType.reference;
	}

	try {
		const appointments = Array.isArray(markerData.appointments) ? markerData.appointments : markerData.appointments && markerData.appointments.length > 0 ? JSON.parse(markerData.appointments) : [];
		const lastVisit = await getLastVisitInfo(markerData.id);
		if (typeof lastVisit === 'object' && Object.keys(lastVisit).length > 0) {
			fields[t('Date_Last_Visit')] = new Date(lastVisit.startDate).toLocaleDateString();
			detailMarkerData.value.lastVisitComment = '';

			if (lastVisit.comment && lastVisit.comment.trim()) {
				detailMarkerData.value.lastVisitComment = lastVisit.comment;
			}

			if (lastVisit._statements && lastVisit._statements.length > 0) {
				fields[t('Date_Last_Statement')] = new Date(lastVisit.startDate).toLocaleDateString();
			}
		} else {
			fields[t('Date_Last_Visit')] = t('NoVisitYet');
		}

		const nextVisit = await getNextVisitInfo(markerData.id);
		if (nextVisit) {
			fields[t('Date_Next_Visit')] = new Date(nextVisit.startDate).toLocaleDateString();
		} else if (appointments && appointments.length > 0) {
			const nextAppointment = appointments[0];
			if (nextAppointment.startDate) {
				fields[t('Date_Next_Visit')] = new Date(nextAppointment.startDate).toLocaleDateString() + ` | ${new Date(nextAppointment.startDate).toLocaleTimeString().substring(0, 5)} - ${new Date(nextAppointment.endDate).toLocaleTimeString().substring(0, 5)}`;
			}
		} else {
			fields[t('Date_Next_Visit')] = t('NoPlannedVisit');
		}

		const frequency = await getVisitFrequency(markerData.id);
		if (frequency) {
			fields[t('CurrentVisitRate')] = frequency;
		}

		const kpis = await getDeliveryLocationKPIs(markerData.id);
		if (kpis && kpis.length > 0) {
			detailMarkerData.value.kpis = kpis;
		}

		const addressParts = [];
		if (markerData.addressLine) addressParts.push(markerData.addressLine);
		if (markerData.addressLine2) addressParts.push(markerData.addressLine2);
		if (markerData.city) addressParts.push(markerData.city);
		if (markerData.postcode) addressParts.push(markerData.postcode);
		if (markerData.country) addressParts.push(markerData.country);
		if (addressParts.length > 0) {
			fields[t('Address')] = addressParts.join(', ');
		}
	} catch (error) {
		console.error('Error loading visit information:', error);
	}

	detailMarkerData.value.fields = fields;
}

function handleActionFocus() {
	if (!isFullscreen.value) {
		toggleFullscreen();
	}
}

function toggleFullscreen() {
	isFullscreen.value = !isFullscreen.value;

	if (!isFullscreen.value && mapRef.value?.hideDetailMarker) {
		mapRef.value.hideDetailMarker();
	}

	eventBus.emit('widget-fullscreened', {
		widget: 'widget-myarea',
		fullScreened: isFullscreen.value,
		callback: async () => {
			resizeWidgetAndMap();
		},
	});
}

function handleWindowResize() {
	setTimeout(resizeWidgetAndMap, 300);
}

function initWidgetResizeObserver() {
	nextTick(() => {
		if (!widgetRootRef.value || typeof ResizeObserver === 'undefined') {
			return;
		}

		widgetResizeObserver = new ResizeObserver((entries) => {
			const widgetEntry = entries[0];
			if (!widgetEntry) {
				return;
			}

			const currentHeight = Math.round(widgetEntry.contentRect.height);
			if (currentHeight > 0 && currentHeight !== lastObservedHeight.value) {
				lastObservedHeight.value = currentHeight;
				resizeWidgetAndMap();
			}
		});

		widgetResizeObserver.observe(widgetRootRef.value);
	});
}

function destroyWidgetResizeObserver() {
	if (widgetResizeObserver) {
		widgetResizeObserver.disconnect();
		widgetResizeObserver = null;
	}
}

function resizeWidgetAndMap() {
	nextTick(() => {
		requestAnimationFrame(() => {
			const doc = iframeDataBridge.getIframeWindow().document;
			const vCard = doc.querySelector('.widget-myarea .v-card');

			if (vCard) {
				const widget = vCard.querySelector('.widget-area');
				const vCardText = vCard.querySelector('.v-card-text');
				if (widget && vCardText) {
					const style = getComputedStyle(vCardText);
					const height = vCardText.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
					widget.style.height = `${height}px`;

					if (mapRef.value && mapRef.value.resize) {
						mapRef.value.resize();
					}
				}
			}
		});
	});
}

// #region MAP LIBRE

function onMapReady() {}

function onMarkerClick(props) {
	isFullscreen.value = true;
	eventBus.emit('widget-fullscreened', {
		widget: 'widget-myarea',
		fullScreened: isFullscreen.value,
		callback: async () => {
			resizeWidgetAndMap();
			setTimeout(async () => {
				await mapRef.value?.resizeDetailMarkerContent();
			}, 300);
		},
	});
}

function onBoundsChanged(b) {}

function onFiltersChanged(newFilteredLocations) {
	deliveryLocationsDisplayed.value = newFilteredLocations || [];
}

// #endregion

// #endregion

defineExpose({
	toggleFullscreen,
	isFullscreen,
});
</script>
